import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { FiFileText, FiPlayCircle, FiInfo } from 'react-icons/fi';
import { fetchAPI } from '../../utils/api';

export default function StudentLessonView() {
    const { monthId } = useParams();
    const [lessons, setLessons] = useState([]);
    const [metadata, setMetadata] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeContent, setActiveContent] = useState(null); // { type: 'video'|'pdf', url: string, title: string }
    const [securitySettings, setSecuritySettings] = useState({ screen_recording_watermark: '1' });

    const getEmbedUrl = (url) => {
        if (!url) return null;
        // YouTube
        const ytRegex = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
        const ytMatch = url.match(ytRegex);
        if (ytMatch && ytMatch[2].length === 11) {
            return `https://www.youtube.com/embed/${ytMatch[2]}?rel=0&modestbranding=1&showinfo=0&autoplay=1`;
        }
        // Vimeo
        const vimeoRegex = /(?:vimeo)\.com.*(?:videos|video|channels|)\/([\d]+)/i;
        const vimeoMatch = url.match(vimeoRegex);
        if (vimeoMatch) {
            return `https://player.vimeo.com/video/${vimeoMatch[1]}?autoplay=1&badge=0&autopause=0&player_id=0&app_id=58479`;
        }
        // Zoom Share Detection
        if (url.includes('zoom.us/rec/share')) {
            return url; // Will be handled by the specialized Zoom mask
        }
        return url;
    };

    const isDirectVideo = (url) => {
        return url && (url.endsWith('.mp4') || url.endsWith('.webm') || url.endsWith('.ogg'));
    };

    const isZoom = (url) => {
        return url && url.includes('zoom.us/rec/share');
    };

    React.useEffect(() => {
        const loadPageData = async () => {
            try {
                // Fetch lessons and settings in parallel
                const [lessonsData, settingsData] = await Promise.all([
                    fetchAPI(`/student/get_lessons.php?month_id=${monthId}`),
                    fetchAPI('/admin/get_settings.php')
                ]);

                setLessons(lessonsData.lessons);
                setMetadata(lessonsData.metadata);
                setSecuritySettings(settingsData);

                // Auto-select first recording if available
                if (lessonsData.lessons.length > 0 && lessonsData.lessons[0].recording_url) {
                    setActiveContent({ type: 'video', url: lessonsData.lessons[0].recording_url, title: lessonsData.lessons[0].title });
                }
            } catch (err) {
                console.error("Failed to load page data", err);
            } finally {
                setLoading(false);
            }
        };
        loadPageData();
    }, [monthId]);

    if (loading) return <div className="p-8 text-center text-gray-500 font-bold">Loading lessons...</div>;
    if (!lessons.length && !metadata) return <div className="p-8 text-center text-gray-400">No lessons available for this month.</div>;

    return (
        <div className="h-[calc(100vh-8rem)] flex flex-col lg:flex-row gap-6 pt-4">
            {/* Left Sidebar: Lesson List */}
            <div className="w-full lg:w-96 bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden flex flex-col">
                <div className="p-4 bg-blue-600 text-white">
                    <h2 className="font-bold text-sm tracking-tight">
                        {metadata?.class_name || 'Mathematics'} - {'Theory Online'}
                    </h2>
                    <p className="text-[10px] opacity-80 uppercase font-black tracking-widest mt-1">
                        Grade {metadata?.grade || 'N/A'} • {metadata?.month_name || 'Education'}
                    </p>
                </div>

                <div className="p-2 border-b border-gray-100">
                    <button className="w-full py-2 bg-gray-50 text-gray-600 text-sm font-medium rounded hover:bg-gray-100 transition-colors flex items-center justify-center gap-2">
                        <FiInfo size={14} /> Show Details
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-4 space-y-6">
                    {lessons.map((lesson) => (
                        <div key={lesson.id} className="group">
                            <h3 className="text-sm font-bold text-gray-800 mb-3">{lesson.title}</h3>
                            <div className="space-y-1 ml-3 border-l-2 border-gray-100 pl-3">
                                {lesson.sub_items.map((sub, idx) => (
                                    <div key={sub.id || idx} className="space-y-0.5 mb-1">
                                        <button
                                            className={`w-full flex items-center gap-2 text-xs py-1 transition-colors text-left ${activeContent?.url === (sub.recording_url || sub.pdf_url) ? 'text-blue-600 font-bold' : 'text-gray-500 hover:text-blue-600'}`}
                                            onClick={() => {
                                                if (sub.pdf_url) setActiveContent({ type: 'pdf', url: sub.pdf_url, title: sub.title, description: sub.description });
                                                else if (sub.recording_url) setActiveContent({ type: 'video', url: sub.recording_url, title: sub.title, description: sub.description });
                                            }}
                                        >
                                            {sub.pdf_url ? <FiFileText /> : <FiPlayCircle />}
                                            <span className="truncate">{sub.title}</span>
                                        </button>
                                        {sub.description && (
                                            <p className="text-[9px] text-gray-400 leading-tight ml-5 border-l border-gray-100 pl-2 line-clamp-1 italic">{sub.description}</p>
                                        )}
                                    </div>
                                ))}
                                {lesson.recording_url && (
                                    <div className="space-y-0.5 mt-2">
                                        <button
                                            className={`w-full flex items-center gap-2 text-xs py-1 transition-colors text-left ${activeContent?.url === lesson.recording_url ? 'text-blue-600 font-black' : 'text-blue-600 font-bold hover:underline'}`}
                                            onClick={() => setActiveContent({ type: 'video', url: lesson.recording_url, title: lesson.title, description: lesson.description })}
                                        >
                                            <FiPlayCircle /> <span>Watch Recording</span>
                                        </button>
                                        {lesson.description && (
                                            <p className="text-[9px] text-blue-500/70 leading-tight ml-5 border-l border-blue-50 pl-2 line-clamp-1 italic">{lesson.description}</p>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Main Area: Player */}
            <div
                className="flex-1 bg-black rounded-2xl overflow-hidden relative group mt-4 lg:mt-0 shadow-2xl"
                onContextMenu={(e) => e.preventDefault()}
            >
                {activeContent ? (
                    activeContent.type === 'video' ? (
                        <div className="w-full h-full bg-black relative flex items-center justify-center">
                            {isDirectVideo(activeContent.url) ? (
                                <video
                                    className="w-full h-full max-h-full"
                                    controls
                                    autoPlay
                                    controlsList="nodownload noplaybackrate"
                                    onContextMenu={(e) => e.preventDefault()}
                                >
                                    <source src={activeContent.url} type="video/mp4" />
                                    Your browser does not support the video tag.
                                </video>
                            ) : isZoom(activeContent.url) ? (
                                <div className="w-full h-full relative overflow-hidden bg-black">
                                    {/* Zoom Header Mask - This hides the top bar where the download button resides */}
                                    <iframe
                                        className="absolute w-full h-[120%] -top-[15%]"
                                        src={activeContent.url}
                                        title={activeContent.title}
                                        frameBorder="0"
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                        allowFullScreen
                                    ></iframe>
                                    {/* Interaction Blocker for Header */}
                                    <div className="absolute top-0 left-0 right-0 h-[10%] bg-transparent z-10"></div>
                                </div>
                            ) : (
                                <iframe
                                    className="w-full h-full"
                                    src={getEmbedUrl(activeContent.url)}
                                    title={activeContent.title}
                                    frameBorder="0"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                    allowFullScreen
                                ></iframe>
                            )}

                            {/* Anti-Recording Watermark */}
                            {securitySettings.screen_recording_watermark === '1' && (
                                <div className="absolute inset-0 pointer-events-none z-20 overflow-hidden">
                                    <span className="absolute text-[10px] text-white/5 font-black uppercase rotate-[-30deg] select-none whitespace-nowrap animate-pulse" style={{ top: '15%', left: '10%' }}>
                                        STUDENT PORTAL • SUDESH MATHS • PROTECTED CONTENT
                                    </span>
                                    <span className="absolute text-[10px] text-white/5 font-black uppercase rotate-[-30deg] select-none whitespace-nowrap" style={{ bottom: '20%', right: '15%' }}>
                                        COPYRIGHT © GD DEVELOPMENT • DO NOT SHARE
                                    </span>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="w-full h-full bg-white flex flex-col">
                            <div className="p-4 bg-gray-50 border-b border-gray-100 flex justify-between items-center">
                                <span className="text-sm font-bold text-gray-700">{activeContent.title}</span>
                                {securitySettings.dev_tools_restricted !== '1' && (
                                    <a href={activeContent.url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 font-bold hover:underline">Open in New Tab</a>
                                )}
                            </div>
                            {activeContent.description && (
                                <div className="px-4 py-2 bg-blue-50 border-b border-blue-100">
                                    <p className="text-[10px] text-blue-600 font-bold uppercase tracking-wider mb-1">Notes / Instructions</p>
                                    <p className="text-xs text-gray-600 leading-relaxed italic">{activeContent.description}</p>
                                </div>
                            )}
                            <div className="flex-1 w-full relative">
                                <iframe
                                    className="w-full h-full"
                                    src={activeContent.url.includes('drive.google.com') ? activeContent.url.replace(/\/view.*$/, '/preview') : activeContent.url}
                                    title={activeContent.title}
                                ></iframe>

                                {/* PDF Shield: Prevents direct interaction with PDF toolbar if restricted */}
                                {securitySettings.dev_tools_restricted === '1' && (
                                    <div className="absolute top-0 left-0 right-0 h-12 bg-transparent z-10"></div>
                                )}
                            </div>
                        </div>
                    )
                ) : (
                    <div className="absolute inset-0 flex items-center justify-center bg-gray-900 text-white">
                        <div className="text-center">
                            <div className="w-20 h-20 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4 border-4 border-white/20">
                                <FiPlayCircle size={40} className="ml-2" />
                            </div>
                            <h2 className="text-2xl font-bold">Sudesh Maths</h2>
                            <p className="opacity-60 text-sm tracking-tight font-medium">Select a lesson to start learning</p>
                        </div>
                    </div>
                )}

                {/* Info Overlay (Visible in Video) */}
                {activeContent?.type === 'video' && activeContent.description && (
                    <div className="absolute top-4 left-4 right-4 z-30 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="bg-black/40 backdrop-blur-md px-6 py-4 rounded-2xl border border-white/5 max-w-lg shadow-2xl">
                            <h4 className="text-white text-sm font-bold mb-1">{activeContent.title}</h4>
                            <p className="text-white/70 text-[11px] leading-relaxed italic line-clamp-3">
                                {activeContent.description}
                            </p>
                            <div className="mt-3 flex items-center gap-2">
                                <span className="w-1 h-1 rounded-full bg-blue-500"></span>
                                <span className="text-[8px] text-white/30 uppercase font-black tracking-widest">Teacher's Note</span>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

